import React, { useState, useEffect } from 'react';
import { ChevronDown, Save, User, Landmark, CreditCard, ChevronRight, IndianRupee, CheckCircle, Building2, CalendarDays, Users, FileText, X, AlertCircle, Download, Trash2, Plus, QrCode } from 'lucide-react';
import QRCode from 'react-qr-code';
import { employeeAPI, leaveConfigAPI, wagesAPI } from '../api/apiService';
import { notifySavedAndReload } from '../utils/notify';
import { useDropdownData } from '../utils/useDropdownData';
import { getLocalToday, getDateYearsAgo } from '../utils/dateUtils';
import DateInput from './DateInput';
import LeaveConfigForm from './LeaveConfigForm';
import EstablishmentCard from './EstablishmentCard';
import './UpdateEmployee.css';
import './AttendanceRegistry.css';

// Fields that require uniqueness validation
const UNIQUE_FIELDS = {
  pan: 'PAN',
  aadhar: 'Aadhaar',
  esicIp: 'ESIC IP',
  uan: 'UAN',
  mobile: 'Mobile Number',
  emergencyContactNo: 'Emergency Contact No',
  email: 'Email',
  pvcReportNo: 'PVC Report No',
  conductCertificate: 'Conduct Certificate No',
  accountNumber: 'Bank Account Number'
};

const UpdateEmployee = ({ defaultEstablishmentId, defaultEmployeeId }) => {
  const [selectedEstablishment, setSelectedEstablishment] = useState('');
  const [selectedEmployee, setSelectedEmployee] = useState('');
  const [activeTab, setActiveTab] = useState('personal');
  const [formData, setFormData] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isLoadingDetails, setIsLoadingDetails] = useState(false);
  const [uniqueErrors, setUniqueErrors] = useState({});
  const [attachments, setAttachments] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [uploadMessage, setUploadMessage] = useState({ type: '', text: '' });
  const [isLoadingAttachments, setIsLoadingAttachments] = useState(false);

  // State for other deductions
  const [otherDeductions, setOtherDeductions] = useState([]);
  const [newDeduction, setNewDeduction] = useState({
    deductionType: '',
    customType: '',
    amount: '',
    effectiveFrom: new Date().toISOString().split('T')[0],
    notes: ''
  });

  // State for other allowances
  const [otherAllowances, setOtherAllowances] = useState([]);
  const [newAllowance, setNewAllowance] = useState({
    allowanceType: '',
    customType: '',
    amount: '',
    effectiveFrom: new Date().toISOString().split('T')[0],
    notes: ''
  });

  // Get today's date for max date validation (using local timezone)
  const today = getLocalToday();
  const maxDOB = getDateYearsAgo(18);

  const standardEducationLevels = ['10th', '12th', 'Diploma', 'Graduate', 'Post-Graduate', 'Doctorate'];

  // Use custom hook for dropdown data
  const {
    establishments,
    employees,
    loading,
    fetchEstablishments,
    fetchEmployeesByEstablishment,
    clearEmployees
  } = useDropdownData();

  // Fetch establishments on component mount
  useEffect(() => {
    fetchEstablishments();
  }, [fetchEstablishments]);

  // Auto-select organization (use prop if provided, else first)
  useEffect(() => {
    if (establishments.length > 0 && !selectedEstablishment) {
      const idToSelect = defaultEstablishmentId
        ? String(defaultEstablishmentId)
        : String(establishments[0].id);
      handleEstablishmentChange({ target: { value: idToSelect } });
    }
  }, [establishments]); // eslint-disable-line react-hooks/exhaustive-deps

  // Auto-select specific employee when defaultEmployeeId prop is provided
  useEffect(() => {
    if (defaultEmployeeId && employees.length > 0 && !selectedEmployee) {
      handleEmployeeChange({ target: { value: String(defaultEmployeeId) } });
    }
  }, [employees]); // eslint-disable-line react-hooks/exhaustive-deps

  const tabs = [
    { id: 'personal', name: 'Personal Details', icon: User },
    { id: 'employment', name: 'Employment Details', icon: Building2 },
    { id: 'banking', name: 'Bank Details', icon: Landmark },
    { id: 'payroll', name: 'Payroll', icon: IndianRupee },
    { id: 'deductions', name: 'Deductions', icon: CreditCard },
    { id: 'leaves', name: 'Leaves', icon: CalendarDays }
  ];

  const handleEstablishmentChange = (e) => {
    const establishmentId = e.target.value;
    setSelectedEstablishment(establishmentId);
    setSelectedEmployee('');
    setActiveTab('personal');
    setFormData({});

    // Fetch employees for selected organization
    if (establishmentId) {
      fetchEmployeesByEstablishment(establishmentId);
    } else {
      clearEmployees();
    }
  };

  const handleEmployeeChange = async (e) => {
    const employeeId = e.target.value;
    setSelectedEmployee(employeeId);
    setActiveTab('personal');

    if (!employeeId) {
      setFormData({});
      return;
    }

    setIsLoadingDetails(true);
    try {
      // Fetch full employee details from API
      const employeeData = await employeeAPI.getById(employeeId);

      // Map API data to form fields
      const formattedData = {
        // Personal Details
        employeeCode: employeeData.employee_id || '',
        civilianExman: employeeData.civilian_exman || 'Civilian', // Default to Civilian if not set
        designation: employeeData.designation || '',
        nameAsPerAadhaar: `${employeeData.first_name || ''} ${employeeData.last_name || ''}`.trim(),
        fatherNameAsPerAadhaar: employeeData.father_name_as_per_aadhaar || '',
        motherName: employeeData.mother_name || '',
        maritalStatus: employeeData.marital_status || '',
        gender: employeeData.gender || '',
        spouseName: employeeData.spouse_name || '',
        dateOfBirth: employeeData.date_of_birth || '',
        dateOfJoining: employeeData.date_of_joining || '',
        age: employeeData.age?.toString() || '',
        educationLevel: employeeData.education_level && standardEducationLevels.includes(employeeData.education_level)
          ? employeeData.education_level
          : (employeeData.education_level ? 'Others' : ''),
        customEducation: employeeData.education_level && !standardEducationLevels.includes(employeeData.education_level)
          ? employeeData.education_level
          : '',
        pan: employeeData.pan || '',
        aadhar: employeeData.aadhar || employeeData.aadhaar || '',
        esicIp: employeeData.esic_ip || '',
        uan: employeeData.uan || '',
        // Communication Address
        commAddressLine1: employeeData.comm_address || '',
        commAddressLine2: employeeData.comm_address_line2 || '',
        commCity: employeeData.comm_city || '',
        commState: employeeData.comm_state || '',
        commPinCode: employeeData.comm_zip_code || '',
        // Permanent Address
        addressLine1: employeeData.address || '',
        addressLine2: employeeData.address_line2 || '',
        city: employeeData.city || '',
        state: employeeData.state || '',
        pinCode: employeeData.zip_code || '',
        mobile: employeeData.mobile || '',
        emergencyContactNo: employeeData.emergency_contact_no || '',
        nextOfKinName: employeeData.next_of_kin_name || '',
        nextOfKinRelation: employeeData.next_of_kin_relation || '',
        email: employeeData.email || '',
        bloodGroup: employeeData.blood_group || '',
        pvcReportNo: employeeData.pvc_report_no || '',
        pvcDate: employeeData.pvc_date || '',
        mcDate: employeeData.mc_date || '',
        conductCertificate: employeeData.conduct_certificate || '',
        vaccination1Date: employeeData.vaccination1_date || '',
        vaccination2Date: employeeData.vaccination2_date || '',
        boosterDate: employeeData.booster_date || '',

        // Barcode
        barcode: employeeData.barcode || '',

        // Bank Details
        accountNumber: employeeData.account_number || '',
        ifsc: employeeData.ifsc || '',
        bankName: employeeData.bank_name || '',

        // Payroll
        department: employeeData.department || '',
        employmentType: employeeData.employment_type || '',
        isManagerStaff: employeeData.is_manager_staff || false,
        contractStartDate: employeeData.date_of_joining || '',
        contractEndDate: employeeData.end_date || '',
        basic: employeeData.basic?.toString() || '',
        da: employeeData.da?.toString() || '',
        hra: employeeData.hra?.toString() || '',
        conveyance: employeeData.conveyance?.toString() || '',
        bonus: employeeData.bonus?.toString() || '',
        otherAllowances: employeeData.other_allowances?.toString() || '',
        payFrequency: employeeData.pay_frequency || '',

        // Deductions
        esi: employeeData.esi?.toString() || '',
        epf: employeeData.epf?.toString() || '',
        esiPercent: employeeData.esi_percent?.toString() || '4',
        epfPercent: employeeData.epf_percent?.toString() || '12',
        employerPfPercent: employeeData.employer_pf_percent?.toString() || '13',
        employerPfAmount: '',
        pt: employeeData.pt?.toString() || '',
        tds: employeeData.tds?.toString() || '',
        society: employeeData.society?.toString() || '',
        insurance: employeeData.insurance?.toString() || '',
        otherDeductions: employeeData.other_deductions?.toString() || '',

        // Leave Configuration (defaults - will be overwritten if config exists)
        isOnProbation: false,
        probationEndDate: '',
        noLeavesDuringProbation: true,
        slBalance: '12',
        clBalance: '12',
        elBalance: '12',
        enableMonthlyAccrual: false,
        slAnnualQuota: '12',
        clAnnualQuota: '12',
        elAnnualQuota: '15',
        slMonthlyAccrual: '1',
        clMonthlyAccrual: '1',
        elMonthlyAccrual: '1.25',
        accrualStartDate: '',
        enableCarryForward: false,
        carryForwardSl: false,
        carryForwardCl: false,
        carryForwardEl: true,
        maxCarryForwardSl: '0',
        maxCarryForwardCl: '0',
        maxCarryForwardEl: '30',
        elEncashable: true,
        minElForEncashment: '10'
      };

      // Fetch leave configuration
      try {
        const leaveConfigResponse = await leaveConfigAPI.getConfig(employeeId);
        if (leaveConfigResponse.exists && leaveConfigResponse.config) {
          const config = leaveConfigResponse.config;
          formattedData.isOnProbation = config.is_on_probation ?? false;
          formattedData.probationEndDate = config.probation_end_date || '';
          formattedData.noLeavesDuringProbation = config.no_leaves_during_probation ?? true;
          formattedData.slBalance = config.sl_balance?.toString() || '12';
          formattedData.clBalance = config.cl_balance?.toString() || '12';
          formattedData.elBalance = config.el_balance?.toString() || '12';
          formattedData.slAnnualQuota = config.sl_annual_quota?.toString() || '12';
          formattedData.clAnnualQuota = config.cl_annual_quota?.toString() || '12';
          formattedData.elAnnualQuota = config.el_annual_quota?.toString() || '15';
          formattedData.slMonthlyAccrual = config.sl_monthly_accrual?.toString() || '1';
          formattedData.clMonthlyAccrual = config.cl_monthly_accrual?.toString() || '1';
          formattedData.elMonthlyAccrual = config.el_monthly_accrual?.toString() || '1.25';
          formattedData.accrualStartDate = config.accrual_start_date || '';
          // Enable monthly accrual if any accrual values exist
          formattedData.enableMonthlyAccrual = !!(config.sl_monthly_accrual || config.cl_monthly_accrual || config.el_monthly_accrual || config.accrual_start_date);
          formattedData.carryForwardSl = config.carry_forward_sl ?? false;
          formattedData.carryForwardCl = config.carry_forward_cl ?? false;
          formattedData.carryForwardEl = config.carry_forward_el ?? true;
          // Enable carry forward if any carry forward is enabled
          formattedData.enableCarryForward = !!(config.carry_forward_sl || config.carry_forward_cl || config.carry_forward_el);
          formattedData.maxCarryForwardSl = config.max_carry_forward_sl?.toString() || '0';
          formattedData.maxCarryForwardCl = config.max_carry_forward_cl?.toString() || '0';
          formattedData.maxCarryForwardEl = config.max_carry_forward_el?.toString() || '30';
          formattedData.elEncashable = config.el_encashable ?? true;
          formattedData.minElForEncashment = config.min_el_for_encashment?.toString() || '10';
        }
      } catch (leaveError) {
        console.error('Error loading leave configuration:', leaveError);
        // Keep default values if leave config doesn't exist
      }

      setFormData(formattedData);

      // Load attachments for this employee
      await loadAttachments(employeeId);

      // Load other deductions for this employee
      await loadOtherDeductions(employeeId);

      // Load other allowances for this employee
      await loadOtherAllowances(employeeId);
    } catch (error) {
      console.error('Error loading employee details:', error);
      alert('Error loading employee details: ' + error.message);
      setFormData({});
    } finally {
      setIsLoadingDetails(false);
    }
  };

  // Load employee attachments
  const loadAttachments = async (employeeId) => {
    if (!employeeId) {
      setAttachments([]);
      return;
    }

    setIsLoadingAttachments(true);
    try {
      const response = await employeeAPI.getAttachments(employeeId);
      setAttachments(response.attachments || []);
    } catch (error) {
      console.error('Error loading attachments:', error);
      setAttachments([]);
    } finally {
      setIsLoadingAttachments(false);
    }
  };

  // Load other deductions for employee
  const loadOtherDeductions = async (employeeId) => {
    if (!employeeId) {
      setOtherDeductions([]);
      return;
    }

    try {
      const response = await wagesAPI.getOtherDeductions({ employee_id: employeeId });
      const deductions = (response.otherDeductions || []).map(ded => ({
        id: ded.id,
        deductionType: ['society', 'insurance'].includes(ded.deduction_type.toLowerCase()) ? ded.deduction_type.toLowerCase() : 'other',
        customType: !['society', 'insurance'].includes(ded.deduction_type.toLowerCase()) ? ded.deduction_type : '',
        amount: ded.amount.toString(),
        effectiveFrom: ded.effective_from || new Date().toISOString().split('T')[0],
        notes: ded.notes || '',
        status: ded.status
      }));
      setOtherDeductions(deductions);
    } catch (error) {
      console.error('Error loading other deductions:', error);
      setOtherDeductions([]);
    }
  };

  const loadOtherAllowances = async (employeeId) => {
    if (!employeeId) {
      setOtherAllowances([]);
      return;
    }

    try {
      const response = await wagesAPI.getOtherAllowances({ employee_id: employeeId });
      const allowances = (response.otherAllowances || []).map(allow => ({
        id: allow.id,
        allowanceType: ['vehicle', 'meal', 'housing'].includes(allow.allowance_type.toLowerCase()) ? allow.allowance_type.toLowerCase() : 'other',
        customType: !['vehicle', 'meal', 'housing'].includes(allow.allowance_type.toLowerCase()) ? allow.allowance_type : '',
        amount: allow.amount.toString(),
        effectiveFrom: allow.effective_from || new Date().toISOString().split('T')[0],
        notes: allow.notes || '',
        status: allow.status
      }));
      setOtherAllowances(allowances);
    } catch (error) {
      console.error('Error loading other allowances:', error);
      setOtherAllowances([]);
    }
  };

  // Auto-calculate age when date of birth changes
  useEffect(() => {
    if (formData.dateOfBirth) {
      // Parse YYYY-MM-DD as local date — new Date(dateOnlyString) treats it
      // as UTC midnight which can shift the day in TZs west of UTC.
      const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(formData.dateOfBirth);
      const dob = m ? new Date(+m[1], +m[2] - 1, +m[3]) : new Date(formData.dateOfBirth);
      const today = new Date();
      let age = today.getFullYear() - dob.getFullYear();
      const monthDiff = today.getMonth() - dob.getMonth();
      if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < dob.getDate())) {
        age--;
      }

      // Validate age is at least 18
      if (age < 18) {
        alert('Employee must be at least 18 years old');
        setFormData(prev => ({ ...prev, dateOfBirth: '', age: '' }));
        return;
      }

      setFormData(prev => ({ ...prev, age: age.toString() }));
    }
  }, [formData.dateOfBirth]);

  // Auto-calculate ESI, EPF, and Employer PF based on Basic + DA
  useEffect(() => {
    const basic = parseFloat(formData.basic) || 0;
    const da = parseFloat(formData.da) || 0;
    const baseAmount = basic + da;

    // ESI = (Basic + DA) × esiPercent%
    const esiPercent = parseFloat(formData.esiPercent) || 4;
    const calculatedEsi = (baseAmount * (esiPercent / 100)).toFixed(2);

    // EPF = (Basic + DA) × epfPercent%
    const epfPercent = parseFloat(formData.epfPercent) || 12;
    const calculatedEpf = (baseAmount * (epfPercent / 100)).toFixed(2);

    // Employer PF = (Basic + DA) × employerPfPercent%
    const employerPfPercent = parseFloat(formData.employerPfPercent) || 13;
    const calculatedEmployerPf = (baseAmount * (employerPfPercent / 100)).toFixed(2);

    setFormData(prev => ({
      ...prev,
      esi: calculatedEsi,
      epf: calculatedEpf,
      employerPfAmount: calculatedEmployerPf
    }));
  }, [formData.basic, formData.da, formData.employerPfPercent]);

  const handleInputChange = (fieldName, value) => {
    setFormData(prev => ({
      ...prev,
      [fieldName]: value
    }));
    // Clear uniqueness error when value changes
    if (UNIQUE_FIELDS[fieldName] && uniqueErrors[fieldName]) {
      setUniqueErrors(prev => ({ ...prev, [fieldName]: null }));
    }
  };

  // Validate field uniqueness on blur
  const validateUniqueness = async (field, value) => {
    if (!UNIQUE_FIELDS[field] || !value || !value.trim()) {
      setUniqueErrors(prev => ({ ...prev, [field]: null }));
      return true;
    }

    // Don't validate if there's already an error for this field to prevent repeated alerts
    if (uniqueErrors[field]) {
      return false;
    }

    try {
      const result = await employeeAPI.checkUnique(field, value, selectedEmployee);
      if (!result.is_unique) {
        const errorMsg = `${UNIQUE_FIELDS[field]} "${value}" is already registered to ${result.existing_employee.name} (${result.existing_employee.employee_id})`;
        setUniqueErrors(prev => ({ ...prev, [field]: errorMsg }));

        // Alert once and focus back on the field
        alert(errorMsg);

        // Focus back on the field
        setTimeout(() => {
          const inputElement = document.querySelector(`input[value="${value}"]`) ||
                              document.querySelector(`[name="${field}"]`);
          if (inputElement) {
            inputElement.focus();
            inputElement.select();
          }
        }, 100);

        return false;
      }
      setUniqueErrors(prev => ({ ...prev, [field]: null }));
      return true;
    } catch (error) {
      console.error('Error checking uniqueness:', error);
      return true; // Allow submission if check fails
    }
  };

  // Validate date fields on blur
  const validateDateField = (field, value, fieldName) => {
    if (!value) return; // Skip validation if empty

    const selectedDate = new Date(value);
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    // Check if date is in the future
    if (selectedDate > today) {
      alert(`${fieldName} cannot be a future date`);
      setFormData(prev => ({ ...prev, [field]: '' }));
      return false;
    }

    // Special validation for Date of Birth
    if (field === 'dateOfBirth') {
      const eighteenYearsAgo = new Date();
      eighteenYearsAgo.setFullYear(eighteenYearsAgo.getFullYear() - 18);

      if (selectedDate > eighteenYearsAgo) {
        alert('Employee must be at least 18 years old');
        setFormData(prev => ({ ...prev, dateOfBirth: '', age: '' }));
        return false;
      }
    }

    return true;
  };

  // File attachment handling functions
  const handleFileSelect = async (e) => {
    const files = Array.from(e.target.files);

    if (!selectedEmployee) {
      setUploadMessage({ type: 'error', text: 'Please select an employee first' });
      e.target.value = '';
      return;
    }

    for (const file of files) {
      // Validate file size (10MB)
      if (file.size > 10 * 1024 * 1024) {
        setUploadMessage({ type: 'error', text: `${file.name} is too large. Maximum size is 10MB` });
        continue;
      }

      // Upload immediately
      try {
        setUploadMessage({ type: 'info', text: `Uploading ${file.name}...` });
        await employeeAPI.uploadAttachment(file, selectedEmployee, selectedCategory);
        setUploadMessage({ type: 'success', text: `${file.name} uploaded successfully` });

        // Reload attachments
        await loadAttachments(selectedEmployee);
      } catch (error) {
        console.error(`Failed to upload ${file.name}:`, error);
        setUploadMessage({ type: 'error', text: `Failed to upload ${file.name}: ${error.message}` });
      }
    }

    // Reset file input and category
    e.target.value = '';
    setSelectedCategory(null);

    // Clear message after 3 seconds
    setTimeout(() => setUploadMessage({ type: '', text: '' }), 3000);
  };

  const handleDeleteAttachment = async (attachmentId) => {
    if (!window.confirm('Are you sure you want to delete this attachment?')) {
      return;
    }

    try {
      setUploadMessage({ type: 'info', text: 'Deleting attachment...' });
      await employeeAPI.deleteAttachment(selectedEmployee, attachmentId);
      setUploadMessage({ type: 'success', text: 'Attachment deleted successfully' });

      // Reload attachments
      await loadAttachments(selectedEmployee);

      setTimeout(() => setUploadMessage({ type: '', text: '' }), 3000);
    } catch (error) {
      console.error('Failed to delete attachment:', error);
      setUploadMessage({ type: 'error', text: `Failed to delete attachment: ${error.message}` });
      setTimeout(() => setUploadMessage({ type: '', text: '' }), 3000);
    }
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i];
  };

  const getFileIcon = (fileName) => {
    const ext = fileName.split('.').pop().toLowerCase();
    if (['jpg', 'jpeg', 'png', 'gif', 'webp', 'svg'].includes(ext)) {
      return '🖼️';
    } else if (ext === 'pdf') {
      return '📄';
    } else if (['doc', 'docx'].includes(ext)) {
      return '📝';
    } else if (['xls', 'xlsx', 'csv'].includes(ext)) {
      return '📊';
    } else if (ext === 'txt') {
      return '📋';
    }
    return '📎';
  };

  const handleNext = () => {
    const currentIndex = tabs.findIndex(t => t.id === activeTab);
    if (currentIndex < tabs.length - 1) {
      const nextTabId = tabs[currentIndex + 1].id;
      setActiveTab(nextTabId);

      // Scroll the active tab into view on mobile
      setTimeout(() => {
        const tabElement = document.querySelector(`.new-employee-tab.active`);
        if (tabElement) {
          tabElement.scrollIntoView({ behavior: 'smooth', inline: 'center', block: 'nearest' });
        }
      }, 100);
    }
  };

  const handlePrevious = () => {
    const currentIndex = tabs.findIndex(t => t.id === activeTab);
    if (currentIndex > 0) {
      const prevTabId = tabs[currentIndex - 1].id;
      setActiveTab(prevTabId);

      // Scroll the active tab into view on mobile
      setTimeout(() => {
        const tabElement = document.querySelector(`.new-employee-tab.active`);
        if (tabElement) {
          tabElement.scrollIntoView({ behavior: 'smooth', inline: 'center', block: 'nearest' });
        }
      }, 100);
    }
  };

  // Validate current tab fields
  const validateCurrentTab = () => {
    const errors = [];

    // Check for unique field errors in current tab only
    const tabUniqueFields = {
      'personal': ['pan', 'aadhar', 'esicIp', 'uan', 'mobile', 'emergencyContactNo', 'email', 'pvcReportNo', 'conductCertificate'],
      'employment': [],
      'banking': ['accountNumber'],
      'payroll': [],
      'deductions': [],
      'leaves': []
    };

    const currentTabUniqueFields = tabUniqueFields[activeTab] || [];
    const tabUniqueErrors = currentTabUniqueFields.filter(field => uniqueErrors[field]);

    if (tabUniqueErrors.length > 0) {
      const errorFieldNames = tabUniqueErrors.map(field => UNIQUE_FIELDS[field]).join(', ');
      errors.push(`Duplicate values found in ${errorFieldNames}`);
    }

    // Add any tab-specific required field validation here if needed
    // For UpdateEmployee, most fields are optional

    return errors;
  };

  const handleSubmit = async () => {
    if (!selectedEstablishment || !selectedEmployee) {
      alert('Please select organization and employee');
      return;
    }

    // Validate only current tab
    const validationErrors = validateCurrentTab();
    if (validationErrors.length > 0) {
      alert(`Please fix the following errors on this tab:\n\n${validationErrors.join('\n')}`);
      return;
    }

    setIsSubmitting(true);

    try {
      // Split name into first and last name
      const nameParts = (formData.nameAsPerAadhaar || '').trim().split(' ');
      const firstName = nameParts[0] || '';
      const lastName = nameParts.slice(1).join(' ') || '';

      // Update personal section with ALL fields
      await employeeAPI.update(selectedEmployee, {
        section: 'personal',
        data: {
          firstName: firstName,
          lastName: lastName,
          nameAsPerAadhaar: formData.nameAsPerAadhaar,
          fatherNameAsPerAadhaar: formData.fatherNameAsPerAadhaar,
          motherName: formData.motherName,
          email: formData.email,
          phone: formData.mobile,
          mobile: formData.mobile,
          dateOfBirth: formData.dateOfBirth,
          dateOfJoin: formData.dateOfJoining,
          age: formData.age ? parseInt(formData.age) : null,
          gender: formData.gender,
          maritalStatus: formData.maritalStatus,
          spouseName: formData.spouseName,
          educationLevel: formData.educationLevel === 'Others' ? formData.customEducation : formData.educationLevel,
          designation: formData.designation,
          civilianExman: formData.civilianExman,
          uan: formData.uan,
          aadhar: formData.aadhar,
          pan: formData.pan,
          esicIp: formData.esicIp,
          // Communication Address
          commAddress: formData.commAddressLine1,
          commAddressLine2: formData.commAddressLine2,
          commCity: formData.commCity,
          commState: formData.commState,
          commZipCode: formData.commPinCode,
          // Permanent Address
          address: formData.addressLine1,
          addressLine2: formData.addressLine2,
          city: formData.city,
          state: formData.state,
          zipCode: formData.pinCode,
          emergencyContactNo: formData.emergencyContactNo,
          nextOfKinName: formData.nextOfKinName,
          nextOfKinRelation: formData.nextOfKinRelation,
          bloodGroup: formData.bloodGroup,
          pvcReportNo: formData.pvcReportNo,
          pvcDate: formData.pvcDate,
          mcDate: formData.mcDate,
          conductCertificate: formData.conductCertificate,
          vaccination1Date: formData.vaccination1Date,
          vaccination2Date: formData.vaccination2Date,
          boosterDate: formData.boosterDate
        }
      });

      // Update banking section
      await employeeAPI.update(selectedEmployee, {
        section: 'banking',
        data: {
          accountNumber: formData.accountNumber,
          ifsc: formData.ifsc,
          bankName: formData.bankName,
          accountType: formData.accountType || 'Savings'
        }
      });

      // Update payroll section with ALL fields
      await employeeAPI.update(selectedEmployee, {
        section: 'payroll',
        data: {
          department: formData.department,
          position: formData.designation,
          employmentType: formData.employmentType,
          isManagerStaff: formData.isManagerStaff || false,
          salary: formData.basic ? parseFloat(formData.basic) : null,
          basic: formData.basic ? parseFloat(formData.basic) : null,
          da: formData.da ? parseFloat(formData.da) : null,
          hra: formData.hra ? parseFloat(formData.hra) : null,
          conveyance: formData.conveyance ? parseFloat(formData.conveyance) : null,
          bonus: formData.bonus ? parseFloat(formData.bonus) : null,
          otherAllowances: formData.otherAllowances ? parseFloat(formData.otherAllowances) : null,
          payFrequency: formData.payFrequency || 'Monthly',
          startDate: formData.contractStartDate || formData.dateOfJoining,
          endDate: formData.contractEndDate,
          esiPercent: formData.esiPercent ? parseFloat(formData.esiPercent) : 4,
          epfPercent: formData.epfPercent ? parseFloat(formData.epfPercent) : 12,
          employerPfPercent: formData.employerPfPercent ? parseFloat(formData.employerPfPercent) : 13
        }
      });

      // Update deductions section
      await employeeAPI.update(selectedEmployee, {
        section: 'deductions',
        data: {
          esic: formData.esi ? parseFloat(formData.esi) : null,
          pf: formData.epf ? parseFloat(formData.epf) : null,
          pt: formData.pt ? parseFloat(formData.pt) : null,
          tds: formData.tds ? parseFloat(formData.tds) : null,
          society: formData.society ? parseFloat(formData.society) : null,
          insurance: formData.insurance ? parseFloat(formData.insurance) : null,
          otherDeductions: formData.otherDeductions ? parseFloat(formData.otherDeductions) : null
        }
      });

      // Update leave configuration
      try {
        const leaveConfig = {
          is_on_probation: formData.isOnProbation,
          probation_end_date: formData.probationEndDate || null,
          no_leaves_during_probation: formData.noLeavesDuringProbation,
          sl_balance: parseFloat(formData.slBalance) || 12,
          cl_balance: parseFloat(formData.clBalance) || 12,
          el_balance: parseFloat(formData.elBalance) || 12,
          // Only save accrual settings if monthly accrual is enabled
          sl_annual_quota: formData.enableMonthlyAccrual ? (parseFloat(formData.slAnnualQuota) || 12) : 12,
          cl_annual_quota: formData.enableMonthlyAccrual ? (parseFloat(formData.clAnnualQuota) || 12) : 12,
          el_annual_quota: formData.enableMonthlyAccrual ? (parseFloat(formData.elAnnualQuota) || 15) : 15,
          sl_monthly_accrual: formData.enableMonthlyAccrual ? (parseFloat(formData.slMonthlyAccrual) || 1) : 0,
          cl_monthly_accrual: formData.enableMonthlyAccrual ? (parseFloat(formData.clMonthlyAccrual) || 1) : 0,
          el_monthly_accrual: formData.enableMonthlyAccrual ? (parseFloat(formData.elMonthlyAccrual) || 1.25) : 0,
          accrual_start_date: formData.enableMonthlyAccrual ? (formData.accrualStartDate || null) : null,
          // Only save carry forward settings if carry forward is enabled
          carry_forward_sl: formData.enableCarryForward ? formData.carryForwardSl : false,
          carry_forward_cl: formData.enableCarryForward ? formData.carryForwardCl : false,
          carry_forward_el: formData.enableCarryForward ? formData.carryForwardEl : false,
          max_carry_forward_sl: formData.enableCarryForward && formData.carryForwardSl ? (parseFloat(formData.maxCarryForwardSl) || 0) : 0,
          max_carry_forward_cl: formData.enableCarryForward && formData.carryForwardCl ? (parseFloat(formData.maxCarryForwardCl) || 0) : 0,
          max_carry_forward_el: formData.enableCarryForward && formData.carryForwardEl ? (parseFloat(formData.maxCarryForwardEl) || 30) : 0,
          el_encashable: formData.elEncashable,
          min_el_for_encashment: formData.elEncashable ? (parseFloat(formData.minElForEncashment) || 10) : 0
        };
        await leaveConfigAPI.saveConfig(selectedEmployee, leaveConfig);
        console.log('Leave configuration saved successfully');
      } catch (leaveError) {
        console.error('Error saving leave configuration:', leaveError);
        alert('Warning: Leave configuration could not be saved: ' + leaveError.message);
      }

      notifySavedAndReload('✓ Employee details updated successfully!');

      // Ask user if they want to update another employee
      const updateAnother = window.confirm('Employee details updated successfully!\n\nDo you want to update another employee?');
      if (updateAnother) {
        setSelectedEmployee('');
        setFormData({});
        setActiveTab('personal');
        setUniqueErrors({});
        setOtherDeductions([]);
        setOtherAllowances([]);
      }
    } catch (error) {
      console.error('Error updating employee details:', error);
      alert('❌ Error updating employee details: ' + error.message);
    } finally {
      setIsSubmitting(false);
    }
  };

  const isLastTab = activeTab === 'leaves';

  return (
    <div className="update-employee-container">
      <div className="update-employee-wrapper">
        {!defaultEmployeeId && (
          <div className="update-employee-header">
            <h2 className="update-employee-title">Update Employee</h2>
            <p className="update-employee-subtitle">Select organization and employee to update details</p>
          </div>
        )}

        {/* Establishment Selector — hidden when pre-selected from Employees page */}
        {!defaultEmployeeId && (
          <EstablishmentCard
            establishments={establishments}
            value={selectedEstablishment}
            onChange={handleEstablishmentChange}
            loading={loading.establishments}
          />
        )}

        {/* Selection Filters — hidden when pre-selected from Employees page */}
        <div className="attendance-grid-filters">
          {selectedEstablishment && !defaultEmployeeId && (
            <div className="attendance-filter-group animate-fadeIn">
              <label className="attendance-filter-label">
                <span className="filter-icon-wrapper employee">
                  <Users size={15} />
                </span>
                Employee
              </label>
              <select
                value={selectedEmployee}
                onChange={handleEmployeeChange}
                className="attendance-filter-select"
                disabled={loading.employees}
              >
                <option value="">
                  {loading.employees ? 'Loading...' : 'Select Employee'}
                </option>
                {employees.map((emp, index) => (
                  <option key={emp.sno || `${emp.employee_id}-${index}`} value={emp.employee_id}>
                    {emp.first_name} {emp.last_name} ({emp.employee_id}) - {emp.department || 'N/A'}
                  </option>
                ))}
                {!loading.employees && employees.length === 0 && (
                  <option value="" disabled>No employees found</option>
                )}
              </select>
            </div>
          )}
        </div>

        {/* Multi-step Form with Tabs */}
        {selectedEmployee && (
          <div className="update-employee-form-card animate-fadeIn">
            {/* Loading Details State */}
            {isLoadingDetails && (
              <div style={{ padding: '2rem', textAlign: 'center', color: '#6b7280' }}>
                Loading employee details...
              </div>
            )}

            {!isLoadingDetails && (
              <>
                {/* Employee Badge with Close Button */}
                <div className="update-employee-section-header" style={{ marginBottom: '1.5rem', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
                    <div className="update-employee-employee-badge">
                      Updating: {employees.find(e => e.employee_id === selectedEmployee)?.first_name} {employees.find(e => e.employee_id === selectedEmployee)?.last_name}
                    </div>
                    {formData.barcode && (
                      <div style={{ position: 'relative' }} className="ue-barcode-wrap">
                        <button
                          type="button"
                          title="View barcode"
                          style={{ display: 'flex', alignItems: 'center', gap: '0.35rem', padding: '0.35rem 0.7rem', background: '#eef2ff', color: '#4338ca', border: '1px solid #c7d2fe', borderRadius: '6px', fontSize: '0.75rem', fontWeight: 600, cursor: 'pointer' }}
                          onClick={(e) => {
                            const popup = e.currentTarget.nextElementSibling;
                            popup.style.display = popup.style.display === 'none' ? 'block' : 'none';
                          }}
                        >
                          <QrCode size={13} /> Barcode
                        </button>
                        <div style={{ display: 'none', position: 'absolute', top: '100%', left: 0, zIndex: 50, marginTop: '0.5rem', background: 'var(--theme-surface)', borderRadius: '10px', padding: '1rem', boxShadow: '0 8px 24px rgba(0,0,0,0.15)', textAlign: 'center', minWidth: '200px' }}>
                          <QRCode value={formData.barcode} size={140} />
                          <div style={{ marginTop: '0.5rem', fontSize: '0.7rem', color: '#4a5568', fontFamily: 'monospace', letterSpacing: '0.03em' }}>{formData.barcode}</div>
                        </div>
                      </div>
                    )}
                  </div>
                  <button
                    type="button"
                    onClick={() => {
                      if (window.confirm('Are you sure you want to close this form? Any unsaved changes will be lost.')) {
                        setSelectedEmployee('');
                        setFormData({});
                        setActiveTab('personal');
                        setUniqueErrors({});
                        setOtherDeductions([]);
                        setOtherAllowances([]);
                      }
                    }}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '0.5rem',
                      padding: '0.5rem 1rem',
                      backgroundColor: '#ef4444',
                      color: 'white',
                      border: 'none',
                      borderRadius: '0.375rem',
                      cursor: 'pointer',
                      fontSize: '0.875rem',
                      fontWeight: '500',
                      transition: 'background-color 0.2s'
                    }}
                    onMouseEnter={(e) => e.target.style.backgroundColor = '#dc2626'}
                    onMouseLeave={(e) => e.target.style.backgroundColor = '#ef4444'}
                  >
                    <X size={16} />
                    Close
                  </button>
                </div>

            {/* Tabs */}
            <div className="new-employee-tabs" style={{ display: 'flex', gap: '0.5rem', marginBottom: '2rem', borderBottom: '2px solid #e5e7eb' }}>
              {tabs.map(tab => {
                const Icon = tab.icon;
                return (
                  <button
                    key={tab.id}
                    onClick={(e) => {
                      setActiveTab(tab.id);
                      // Scroll the clicked tab into view on mobile
                      setTimeout(() => {
                        e.target.closest('button').scrollIntoView({ behavior: 'smooth', inline: 'center', block: 'nearest' });
                      }, 100);
                    }}
                    className={`new-employee-tab ${activeTab === tab.id ? 'active' : ''}`}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '0.5rem',
                      padding: '0.75rem 1.5rem',
                      background: activeTab === tab.id ? '#10b981' : 'transparent',
                      color: activeTab === tab.id ? 'white' : '#6b7280',
                      border: 'none',
                      borderRadius: '8px 8px 0 0',
                      cursor: 'pointer',
                      fontSize: '0.875rem',
                      fontWeight: activeTab === tab.id ? '600' : '500',
                      transition: 'all 0.2s',
                      position: 'relative'
                    }}
                  >
                    <Icon size={16} />
                    {tab.name}
                    {activeTab === tab.id && (
                      <div style={{
                        position: 'absolute',
                        bottom: '-2px',
                        left: 0,
                        right: 0,
                        height: '2px',
                        background: '#10b981'
                      }}></div>
                    )}
                  </button>
                );
              })}
            </div>

            <div className="new-employee-content">
              {/* Personal Details Tab */}
              {activeTab === 'personal' && (
                <div className="animate-fadeIn">
                  <h3 className="new-employee-section-header" style={{ marginBottom: '0.5rem', fontSize: '1.125rem', fontWeight: '600' }}>Personal Details</h3>
                  <p className="new-employee-section-description" style={{ marginBottom: '1.5rem', color: '#6b7280', fontSize: '0.875rem' }}>Update the employee's complete information.</p>

                  {/* Name and Family Details */}
                  <div className="update-employee-form-grid" style={{ gridTemplateColumns: '1fr' }}>
                    <div>
                      <label className="update-employee-field-label">Name As Per Aadhaar</label>
                      <input type="text" value={formData.nameAsPerAadhaar || ''} onChange={(e) => handleInputChange('nameAsPerAadhaar', e.target.value)} className="update-employee-input" />
                    </div>
                  </div>

                  <div className="update-employee-form-grid">
                    <div>
                      <label className="update-employee-field-label">Father Name As Per Aadhaar</label>
                      <input type="text" value={formData.fatherNameAsPerAadhaar || ''} onChange={(e) => handleInputChange('fatherNameAsPerAadhaar', e.target.value)} className="update-employee-input" />
                    </div>
                    <div>
                      <label className="update-employee-field-label">Mother Name</label>
                      <input type="text" value={formData.motherName || ''} onChange={(e) => handleInputChange('motherName', e.target.value)} className="update-employee-input" />
                    </div>
                  </div>

                  <div className="update-employee-form-grid" style={{ gridTemplateColumns: 'repeat(3, 1fr)' }}>
                    <div>
                      <label className="update-employee-field-label">Marital Status</label>
                      <select value={formData.maritalStatus || ''} onChange={(e) => handleInputChange('maritalStatus', e.target.value)} className="update-employee-select">
                        <option value="">Select</option>
                        <option value="Single">Single</option>
                        <option value="Married">Married</option>
                        <option value="Divorced">Divorced</option>
                        <option value="Widowed">Widowed</option>
                      </select>
                    </div>
                    <div>
                      <label className="update-employee-field-label">Gender</label>
                      <select value={formData.gender || ''} onChange={(e) => handleInputChange('gender', e.target.value)} className="update-employee-select">
                        <option value="">Select</option>
                        <option value="Male">Male</option>
                        <option value="Female">Female</option>
                        <option value="Other">Other</option>
                      </select>
                    </div>
                    <div>
                      <label className="update-employee-field-label">Wife/Husband Name</label>
                      <input type="text" value={formData.spouseName || ''} onChange={(e) => handleInputChange('spouseName', e.target.value)} className="update-employee-input" />
                    </div>
                  </div>

                  {/* Date and Education */}
                  <div className="update-employee-form-grid" style={{ gridTemplateColumns: 'repeat(3, 1fr)' }}>
                    <div>
                      <label className="update-employee-field-label">Date of Birth</label>
                      <DateInput
                        value={formData.dateOfBirth || ''}
                        onChange={(e) => handleInputChange('dateOfBirth', e.target.value)}
                        onBlur={(e) => validateDateField('dateOfBirth', e.target.value, 'Date of Birth')}
                        max={maxDOB}
                        className="update-employee-input"
                      />
                    </div>
                    <div>
                      <label className="update-employee-field-label">Date of Joining</label>
                      <DateInput value={formData.dateOfJoining || ''} onChange={(e) => handleInputChange('dateOfJoining', e.target.value)} onBlur={(e) => validateDateField('dateOfJoining', e.target.value, 'Date of Joining')} max={today} className="update-employee-input" />
                    </div>
                    <div>
                      <label className="update-employee-field-label">Age</label>
                      <input type="text" value={formData.age || ''} readOnly className="update-employee-input" style={{ backgroundColor: 'var(--theme-surfaceHover)' }} />
                    </div>
                  </div>

                  {/* Designation - moved here above Education Level */}
                  <div className="update-employee-form-grid" style={{ gridTemplateColumns: '1fr' }}>
                    <div>
                      <label className="update-employee-field-label">Designation</label>
                      <input type="text" value={formData.designation || ''} onChange={(e) => handleInputChange('designation', e.target.value)} className="update-employee-input" />
                    </div>
                  </div>

                  <div className="update-employee-form-grid" style={{ gridTemplateColumns: '1fr' }}>
                    <div>
                      <label className="update-employee-field-label">Education Level</label>
                      <select value={formData.educationLevel || ''} onChange={(e) => handleInputChange('educationLevel', e.target.value)} className="update-employee-select">
                        <option value="">Select</option>
                        <option value="10th">10th Standard</option>
                        <option value="12th">12th Standard</option>
                        <option value="Diploma">Diploma</option>
                        <option value="Graduate">Graduate</option>
                        <option value="Post-Graduate">Post-Graduate</option>
                        <option value="Doctorate">Doctorate</option>
                        <option value="Others">Others</option>
                      </select>
                    </div>
                    {formData.educationLevel === 'Others' && (
                      <div className="animate-fadeIn" style={{ marginTop: '1rem' }}>
                        <label className="update-employee-field-label">Specify Education Level</label>
                        <input
                          type="text"
                          value={formData.customEducation || ''}
                          onChange={(e) => handleInputChange('customEducation', e.target.value)}
                          className="update-employee-input"
                          placeholder="Enter custom education level"
                        />
                      </div>
                    )}
                  </div>

                  {/* Government IDs */}
                  <div className="update-employee-form-grid">
                    <div>
                      <label className="update-employee-field-label">PAN</label>
                      <input type="text" value={formData.pan || ''} onChange={(e) => handleInputChange('pan', e.target.value.toUpperCase())} onBlur={(e) => validateUniqueness('pan', e.target.value)} className={`update-employee-input ${uniqueErrors.pan ? 'input-error' : ''}`} maxLength="10" />
                      {uniqueErrors.pan && <span className="error-text">{uniqueErrors.pan}</span>}
                    </div>
                    <div>
                      <label className="update-employee-field-label">Aadhaar</label>
                      <input type="text" value={formData.aadhar || ''} onChange={(e) => handleInputChange('aadhar', e.target.value)} onBlur={(e) => validateUniqueness('aadhar', e.target.value)} className={`update-employee-input ${uniqueErrors.aadhar ? 'input-error' : ''}`} maxLength="12" />
                      {uniqueErrors.aadhar && <span className="error-text">{uniqueErrors.aadhar}</span>}
                    </div>
                  </div>

                  <div className="update-employee-form-grid">
                    <div>
                      <label className="update-employee-field-label">ESIC IP</label>
                      <input type="text" value={formData.esicIp || ''} onChange={(e) => handleInputChange('esicIp', e.target.value)} onBlur={(e) => validateUniqueness('esicIp', e.target.value)} className={`update-employee-input ${uniqueErrors.esicIp ? 'input-error' : ''}`} />
                      {uniqueErrors.esicIp && <span className="error-text">{uniqueErrors.esicIp}</span>}
                    </div>
                    <div>
                      <label className="update-employee-field-label">UAN</label>
                      <input type="text" value={formData.uan || ''} onChange={(e) => handleInputChange('uan', e.target.value)} onBlur={(e) => validateUniqueness('uan', e.target.value)} className={`update-employee-input ${uniqueErrors.uan ? 'input-error' : ''}`} />
                      {uniqueErrors.uan && <span className="error-text">{uniqueErrors.uan}</span>}
                    </div>
                  </div>

                  {/* Communication Address */}
                  <h4 style={{ marginTop: '1.5rem', marginBottom: '0.75rem', fontWeight: '600' }}>Communication Address</h4>
                  <div className="update-employee-form-grid" style={{ gridTemplateColumns: '1fr' }}>
                    <div>
                      <label className="update-employee-field-label">Address Line 1</label>
                      <input type="text" value={formData.commAddressLine1 || ''} onChange={(e) => handleInputChange('commAddressLine1', e.target.value)} className="update-employee-input" />
                    </div>
                  </div>

                  <div className="update-employee-form-grid" style={{ gridTemplateColumns: '1fr' }}>
                    <div>
                      <label className="update-employee-field-label">Address Line 2</label>
                      <input type="text" value={formData.commAddressLine2 || ''} onChange={(e) => handleInputChange('commAddressLine2', e.target.value)} className="update-employee-input" />
                    </div>
                  </div>

                  <div className="update-employee-form-grid" style={{ gridTemplateColumns: 'repeat(3, 1fr)' }}>
                    <div>
                      <label className="update-employee-field-label">City</label>
                      <input type="text" value={formData.commCity || ''} onChange={(e) => handleInputChange('commCity', e.target.value)} className="update-employee-input" />
                    </div>
                    <div>
                      <label className="update-employee-field-label">State</label>
                      <input type="text" value={formData.commState || ''} onChange={(e) => handleInputChange('commState', e.target.value)} className="update-employee-input" />
                    </div>
                    <div>
                      <label className="update-employee-field-label">PIN Code</label>
                      <input type="text" value={formData.commPinCode || ''} onChange={(e) => handleInputChange('commPinCode', e.target.value)} className="update-employee-input" maxLength="6" />
                    </div>
                  </div>

                  {/* Permanent Address */}
                  <h4 style={{ marginTop: '1.5rem', marginBottom: '0.75rem', fontWeight: '600' }}>Permanent Address</h4>
                  <div className="update-employee-form-grid" style={{ gridTemplateColumns: '1fr' }}>
                    <div>
                      <label className="update-employee-field-label">Address Line 1</label>
                      <input type="text" value={formData.addressLine1 || ''} onChange={(e) => handleInputChange('addressLine1', e.target.value)} className="update-employee-input" />
                    </div>
                  </div>

                  <div className="update-employee-form-grid" style={{ gridTemplateColumns: '1fr' }}>
                    <div>
                      <label className="update-employee-field-label">Address Line 2</label>
                      <input type="text" value={formData.addressLine2 || ''} onChange={(e) => handleInputChange('addressLine2', e.target.value)} className="update-employee-input" />
                    </div>
                  </div>

                  <div className="update-employee-form-grid" style={{ gridTemplateColumns: 'repeat(3, 1fr)' }}>
                    <div>
                      <label className="update-employee-field-label">City</label>
                      <input type="text" value={formData.city || ''} onChange={(e) => handleInputChange('city', e.target.value)} className="update-employee-input" />
                    </div>
                    <div>
                      <label className="update-employee-field-label">State</label>
                      <input type="text" value={formData.state || ''} onChange={(e) => handleInputChange('state', e.target.value)} className="update-employee-input" />
                    </div>
                    <div>
                      <label className="update-employee-field-label">PIN Code</label>
                      <input type="text" value={formData.pinCode || ''} onChange={(e) => handleInputChange('pinCode', e.target.value)} className="update-employee-input" maxLength="6" />
                    </div>
                  </div>

                  {/* Contact Details */}
                  <h4 style={{ marginTop: '1.5rem', marginBottom: '0.75rem', fontWeight: '600' }}>Contact Details</h4>
                  <div className="update-employee-form-grid">
                    <div>
                      <label className="update-employee-field-label">Mobile</label>
                      <input type="tel" value={formData.mobile || ''} onChange={(e) => handleInputChange('mobile', e.target.value)} onBlur={(e) => validateUniqueness('mobile', e.target.value)} className={`update-employee-input ${uniqueErrors.mobile ? 'input-error' : ''}`} />
                      {uniqueErrors.mobile && <span className="error-text">{uniqueErrors.mobile}</span>}
                    </div>
                    <div>
                      <label className="update-employee-field-label">Emergency Contact No.</label>
                      <input type="tel" value={formData.emergencyContactNo || ''} onChange={(e) => handleInputChange('emergencyContactNo', e.target.value)} onBlur={(e) => validateUniqueness('emergencyContactNo', e.target.value)} className={`update-employee-input ${uniqueErrors.emergencyContactNo ? 'input-error' : ''}`} />
                      {uniqueErrors.emergencyContactNo && <span className="error-text">{uniqueErrors.emergencyContactNo}</span>}
                    </div>
                  </div>

                  <div className="update-employee-form-grid">
                    <div>
                      <label className="update-employee-field-label">Next of Kin/Nominee Name</label>
                      <input type="text" value={formData.nextOfKinName || ''} onChange={(e) => handleInputChange('nextOfKinName', e.target.value)} className="update-employee-input" />
                    </div>
                    <div>
                      <label className="update-employee-field-label">Next of Kin/Nominee Relation</label>
                      <input type="text" value={formData.nextOfKinRelation || ''} onChange={(e) => handleInputChange('nextOfKinRelation', e.target.value)} className="update-employee-input" />
                    </div>
                  </div>

                  <div className="update-employee-form-grid">
                    <div>
                      <label className="update-employee-field-label">Email</label>
                      <input type="email" value={formData.email || ''} onChange={(e) => handleInputChange('email', e.target.value)} onBlur={(e) => validateUniqueness('email', e.target.value)} className={`update-employee-input ${uniqueErrors.email ? 'input-error' : ''}`} />
                      {uniqueErrors.email && <span className="error-text">{uniqueErrors.email}</span>}
                    </div>
                    <div>
                      <label className="update-employee-field-label">Blood Group</label>
                      <select value={formData.bloodGroup || ''} onChange={(e) => handleInputChange('bloodGroup', e.target.value)} className="update-employee-select">
                        <option value="">Select</option>
                        <option value="A+">A+</option>
                        <option value="A-">A-</option>
                        <option value="B+">B+</option>
                        <option value="B-">B-</option>
                        <option value="AB+">AB+</option>
                        <option value="AB-">AB-</option>
                        <option value="O+">O+</option>
                        <option value="O-">O-</option>
                      </select>
                    </div>
                  </div>

                  {/* Police Verification Certificate (PVC) */}
                  <h4 style={{ marginTop: '1.5rem', marginBottom: '0.75rem', fontWeight: '600' }}>Police Verification Certificate (PVC)</h4>
                  <div className="update-employee-form-grid">
                    <div>
                      <label className="update-employee-field-label">PVC Report No.</label>
                      <input type="text" value={formData.pvcReportNo || ''} onChange={(e) => handleInputChange('pvcReportNo', e.target.value)} onBlur={(e) => validateUniqueness('pvcReportNo', e.target.value)} className={`update-employee-input ${uniqueErrors.pvcReportNo ? 'input-error' : ''}`} />
                      {uniqueErrors.pvcReportNo && <span className="error-text">{uniqueErrors.pvcReportNo}</span>}
                    </div>
                    <div>
                      <label className="update-employee-field-label">PVC Date</label>
                      <DateInput value={formData.pvcDate || ''} onChange={(e) => handleInputChange('pvcDate', e.target.value)} onBlur={(e) => validateDateField('pvcDate', e.target.value, 'PVC Date')} max={today} className="update-employee-input" />
                    </div>
                  </div>

                  {/* Medical & Compliance */}
                  <h4 style={{ marginTop: '1.5rem', marginBottom: '0.75rem', fontWeight: '600' }}>Medical & Compliance</h4>
                  <div className="update-employee-form-grid">
                    <div>
                      <label className="update-employee-field-label">MC Date</label>
                      <DateInput value={formData.mcDate || ''} onChange={(e) => handleInputChange('mcDate', e.target.value)} onBlur={(e) => validateDateField('mcDate', e.target.value, 'MC Date')} max={today} className="update-employee-input" />
                    </div>
                    <div>
                      <label className="update-employee-field-label">Conduct Certificate</label>
                      <input type="text" value={formData.conductCertificate || ''} onChange={(e) => handleInputChange('conductCertificate', e.target.value)} onBlur={(e) => validateUniqueness('conductCertificate', e.target.value)} className={`update-employee-input ${uniqueErrors.conductCertificate ? 'input-error' : ''}`} />
                      {uniqueErrors.conductCertificate && <span className="error-text">{uniqueErrors.conductCertificate}</span>}
                    </div>
                  </div>

                  <div className="update-employee-form-grid" style={{ gridTemplateColumns: 'repeat(3, 1fr)' }}>
                    <div>
                      <label className="update-employee-field-label">Vaccination 1 (Date)</label>
                      <DateInput value={formData.vaccination1Date || ''} onChange={(e) => handleInputChange('vaccination1Date', e.target.value)} onBlur={(e) => validateDateField('vaccination1Date', e.target.value, 'Vaccination 1 Date')} max={today} className="update-employee-input" />
                    </div>
                    <div>
                      <label className="update-employee-field-label">Vaccination 2 (Date)</label>
                      <DateInput value={formData.vaccination2Date || ''} onChange={(e) => handleInputChange('vaccination2Date', e.target.value)} onBlur={(e) => validateDateField('vaccination2Date', e.target.value, 'Vaccination 2 Date')} max={today} className="update-employee-input" />
                    </div>
                    <div>
                      <label className="update-employee-field-label">Booster (Date)</label>
                      <DateInput value={formData.boosterDate || ''} onChange={(e) => handleInputChange('boosterDate', e.target.value)} onBlur={(e) => validateDateField('boosterDate', e.target.value, 'Booster Date')} max={today} className="update-employee-input" />
                    </div>
                  </div>

                  {/* Ex-Serviceman Checkbox */}
                  <div style={{
                    marginTop: '2rem',
                    padding: '1.25rem',
                    backgroundColor: '#F9FAFB',
                    borderRadius: '0.5rem',
                    border: '2px solid #E5E7EB'
                  }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                      <input
                        type="checkbox"
                        id="exServiceman"
                        checked={formData.civilianExman === 'Ex-serviceman'}
                        onChange={(e) => {
                          handleInputChange('civilianExman', e.target.checked ? 'Ex-serviceman' : 'Civilian');
                        }}
                        style={{
                          width: '20px',
                          height: '20px',
                          cursor: 'pointer',
                          accentColor: '#3B82F6'
                        }}
                      />
                      <label
                        htmlFor="exServiceman"
                        style={{
                          fontSize: '1rem',
                          fontWeight: '500',
                          color: '#374151',
                          cursor: 'pointer',
                          userSelect: 'none'
                        }}
                      >
                        Ex-serviceman
                      </label>
                    </div>
                    <p style={{
                      marginTop: '0.5rem',
                      marginLeft: '2rem',
                      fontSize: '0.875rem',
                      color: '#6B7280'
                    }}>
                      Check this box if the employee is a former member of the armed forces
                    </p>
                  </div>

                  {/* Attachments Section */}
                  <div className="new-employee-field-group" style={{ marginTop: '2rem' }}>
                    <h3 className="new-employee-section-header">Attachments</h3>
                    <p className="new-employee-section-description">Upload and manage employee photos and documents.</p>

                    <div className="establishment-attachments-section" style={{
                      marginTop: '1rem',
                      padding: '1rem',
                      border: '1px dashed var(--theme-primary)',
                      borderRadius: '0.5rem',
                      background: 'var(--theme-surfaceHover)'
                    }}>
                      {/* Category Selection */}
                      <div style={{ marginBottom: '0.75rem' }}>
                        <label style={{ fontSize: '0.875rem', fontWeight: '500', color: '#374151', marginBottom: '0.5rem', display: 'block' }}>
                          File Category
                        </label>
                        <div style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap' }}>
                          <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer' }}>
                            <input
                              type="radio"
                              name="fileCategoryUpdate"
                              checked={selectedCategory === null}
                              onChange={() => setSelectedCategory(null)}
                              style={{ cursor: 'pointer', width: '16px', height: '16px' }}
                            />
                            <span style={{ fontSize: '0.875rem', color: '#374151' }}>General</span>
                          </label>
                          <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer' }}>
                            <input
                              type="radio"
                              name="fileCategoryUpdate"
                              checked={selectedCategory === 'photo'}
                              onChange={() => setSelectedCategory('photo')}
                              style={{ cursor: 'pointer', width: '16px', height: '16px' }}
                            />
                            <span style={{ fontSize: '0.875rem', color: '#374151' }}>Photo</span>
                          </label>
                          <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer' }}>
                            <input
                              type="radio"
                              name="fileCategoryUpdate"
                              checked={selectedCategory === 'document'}
                              onChange={() => setSelectedCategory('document')}
                              style={{ cursor: 'pointer', width: '16px', height: '16px' }}
                            />
                            <span style={{ fontSize: '0.875rem', color: '#374151' }}>Document</span>
                          </label>
                          <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer' }}>
                            <input
                              type="radio"
                              name="fileCategoryUpdate"
                              checked={selectedCategory === 'certificate'}
                              onChange={() => setSelectedCategory('certificate')}
                              style={{ cursor: 'pointer', width: '16px', height: '16px' }}
                            />
                            <span style={{ fontSize: '0.875rem', color: '#374151' }}>Certificate</span>
                          </label>
                        </div>
                      </div>

                      {/* Upload Button */}
                      <div style={{ marginBottom: '1rem' }}>
                        <label
                          htmlFor="update-employee-attachment-upload"
                          style={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: '0.5rem',
                            padding: '0.625rem 1.25rem',
                            cursor: 'pointer',
                            background: 'var(--theme-surface)',
                            color: '#059669',
                            border: '2px solid #059669',
                            borderRadius: '8px',
                            fontSize: '0.875rem',
                            fontWeight: '600',
                            transition: 'all 0.2s ease'
                          }}
                          onMouseEnter={(e) => {
                            e.currentTarget.style.background = '#f0fdf4';
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.background = '#ffffff';
                          }}
                        >
                          <FileText size={16} />
                          Choose Files
                        </label>
                        <input
                          id="update-employee-attachment-upload"
                          type="file"
                          multiple
                          accept="*"
                          onChange={handleFileSelect}
                          style={{ display: 'none' }}
                        />
                        <p style={{ fontSize: '0.75rem', color: '#6b7280', marginTop: '0.5rem' }}>
                          All file types supported. Max 10MB per file.
                        </p>
                      </div>

                      {/* Upload Message */}
                      {uploadMessage.text && (
                        <div style={{
                          padding: '0.75rem',
                          borderRadius: '0.375rem',
                          marginBottom: '1rem',
                          background: uploadMessage.type === 'success' ? '#d1fae5' : uploadMessage.type === 'error' ? '#fee2e2' : '#dbeafe',
                          color: uploadMessage.type === 'success' ? '#065f46' : uploadMessage.type === 'error' ? '#991b1b' : '#1e40af',
                          border: `1px solid ${uploadMessage.type === 'success' ? '#10b981' : uploadMessage.type === 'error' ? '#ef4444' : '#3b82f6'}`,
                          display: 'flex',
                          alignItems: 'center',
                          gap: '0.5rem',
                          fontSize: '0.875rem'
                        }}>
                          {uploadMessage.type === 'success' ? <CheckCircle size={16} /> : <AlertCircle size={16} />}
                          {uploadMessage.text}
                        </div>
                      )}

                      {/* Attachments List */}
                      {isLoadingAttachments ? (
                        <div style={{
                          padding: '2rem',
                          textAlign: 'center',
                          color: '#6b7280',
                          fontSize: '0.875rem'
                        }}>
                          Loading attachments...
                        </div>
                      ) : attachments.length > 0 ? (
                        <div>
                          <h4 style={{
                            color: '#059669',
                            fontWeight: '600',
                            marginBottom: '0.75rem',
                            fontSize: '0.875rem'
                          }}>
                            Uploaded Files ({attachments.length})
                          </h4>
                          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                            {attachments.map((attachment) => (
                              <div
                                key={attachment.id}
                                style={{
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'space-between',
                                  padding: '0.75rem',
                                  background: 'var(--theme-surface)',
                                  border: '1px solid #e5e7eb',
                                  borderRadius: '0.375rem'
                                }}
                              >
                                <div style={{
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: '0.75rem',
                                  flex: 1
                                }}>
                                  <span style={{ fontSize: '1.5rem' }}>
                                    {getFileIcon(attachment.file_name)}
                                  </span>
                                  <div style={{ flex: 1, minWidth: 0 }}>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', flexWrap: 'wrap' }}>
                                      <span style={{
                                        color: '#374151',
                                        fontWeight: '500',
                                        fontSize: '0.875rem',
                                        overflow: 'hidden',
                                        textOverflow: 'ellipsis',
                                        whiteSpace: 'nowrap'
                                      }}>
                                        {attachment.file_name}
                                      </span>
                                      {attachment.category && (
                                        <span style={{
                                          padding: '0.125rem 0.5rem',
                                          background: attachment.category === 'photo' ? '#dbeafe' : attachment.category === 'document' ? '#fef3c7' : '#fce7f3',
                                          color: attachment.category === 'photo' ? '#1e40af' : attachment.category === 'document' ? '#92400e' : '#9f1239',
                                          borderRadius: '0.25rem',
                                          fontSize: '0.625rem',
                                          fontWeight: '600',
                                          textTransform: 'uppercase',
                                          whiteSpace: 'nowrap'
                                        }}>
                                          {attachment.category}
                                        </span>
                                      )}
                                    </div>
                                    <p style={{
                                      fontSize: '0.75rem',
                                      color: '#6b7280',
                                      marginTop: '0.125rem'
                                    }}>
                                      {formatFileSize(attachment.file_size)} • Uploaded {new Date(attachment.uploaded_at).toLocaleDateString()}
                                    </p>
                                  </div>
                                </div>
                                <div style={{ display: 'flex', gap: '0.5rem' }}>
                                  <a
                                    href={attachment.s3_url}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    style={{
                                      color: '#059669',
                                      cursor: 'pointer',
                                      background: 'none',
                                      border: 'none',
                                      padding: '0.375rem',
                                      display: 'flex',
                                      alignItems: 'center',
                                      transition: 'color 0.2s',
                                      textDecoration: 'none'
                                    }}
                                    title="Download file"
                                  >
                                    <Download size={18} />
                                  </a>
                                  <button
                                    onClick={() => handleDeleteAttachment(attachment.id)}
                                    style={{
                                      color: '#ef4444',
                                      cursor: 'pointer',
                                      background: 'none',
                                      border: 'none',
                                      padding: '0.375rem',
                                      display: 'flex',
                                      alignItems: 'center',
                                      transition: 'color 0.2s'
                                    }}
                                    title="Delete file"
                                  >
                                    <Trash2 size={18} />
                                  </button>
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      ) : (
                        <div style={{
                          padding: '2rem',
                          textAlign: 'center',
                          color: '#6b7280',
                          fontSize: '0.875rem'
                        }}>
                          No files uploaded yet
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              )}

              {/* Employment Details Tab */}
              {activeTab === 'employment' && (
                <div className="animate-fadeIn">
                  <h3 className="new-employee-section-header" style={{ marginBottom: '0.5rem', fontSize: '1.125rem', fontWeight: '600' }}>Employment Details</h3>
                  <p className="new-employee-section-description" style={{ marginBottom: '1.5rem', color: '#6b7280', fontSize: '0.875rem' }}>Update organization, department, and employment type.</p>

                  <div className="update-employee-form-grid">
                    <div>
                      <label className="update-employee-field-label">Organization</label>
                      <input
                        type="text"
                        value={establishments.find(e => e.id === parseInt(selectedEstablishment))?.name || 'N/A'}
                        readOnly
                        className="update-employee-input"
                        style={{ backgroundColor: 'var(--theme-surfaceHover)', cursor: 'not-allowed' }}
                      />
                      <p style={{ fontSize: '0.75rem', color: '#6b7280', marginTop: '0.25rem' }}>
                        Organization cannot be changed after employee creation
                      </p>
                    </div>
                    <div>
                      <label className="update-employee-field-label">Department</label>
                      <input
                        type="text"
                        value={formData.department || ''}
                        onChange={(e) => handleInputChange('department', e.target.value)}
                        className="update-employee-input"
                        placeholder="Enter department"
                      />
                    </div>
                  </div>

                  <div className="update-employee-form-grid" style={{ marginTop: '1.5rem' }}>
                    <div>
                      <label className="update-employee-field-label">Employment Type</label>
                      <select value={formData.employmentType || ''} onChange={(e) => handleInputChange('employmentType', e.target.value)} className="update-employee-select">
                        <option value="">Select Employment Type</option>
                        <option value="full-time">Full-time</option>
                        <option value="part-time">Part-time</option>
                        <option value="contract">Contract</option>
                        <option value="temporary">Temporary</option>
                      </select>
                    </div>
                  </div>

                  {/* Manager Staff Checkbox */}
                  <div className="update-employee-form-grid" style={{ gridTemplateColumns: '1fr', marginTop: '1.5rem' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                      <input
                        type="checkbox"
                        id="isManagerStaff"
                        checked={formData.isManagerStaff || false}
                        onChange={(e) => handleInputChange('isManagerStaff', e.target.checked)}
                        style={{
                          width: '18px',
                          height: '18px',
                          cursor: 'pointer',
                          accentColor: '#059669'
                        }}
                      />
                      <label
                        htmlFor="isManagerStaff"
                        className="update-employee-field-label"
                        style={{ margin: 0, cursor: 'pointer', fontWeight: '500' }}
                      >
                        Manager Staff
                      </label>
                    </div>
                    <p style={{ fontSize: '0.75rem', color: '#6b7280', marginTop: '0.25rem', marginLeft: '1.65rem' }}>
                      Check this if the employee is a manager or supervisory staff
                    </p>
                  </div>

                  <h4 style={{ marginTop: '2rem', marginBottom: '0.75rem', fontWeight: '600' }}>Contract Period</h4>
                  <div className="update-employee-form-grid">
                    <div>
                      <label className="update-employee-field-label">Contract Start Date</label>
                      <DateInput
                        value={formData.contractStartDate || ''}
                        onChange={(e) => handleInputChange('contractStartDate', e.target.value)}
                        className="update-employee-input"
                      />
                    </div>
                    <div>
                      <label className="update-employee-field-label">Contract End Date</label>
                      <DateInput
                        value={formData.contractEndDate || ''}
                        onChange={(e) => handleInputChange('contractEndDate', e.target.value)}
                        className="update-employee-input"
                      />
                    </div>
                  </div>
                </div>
              )}

              {/* Bank Details Tab */}
              {activeTab === 'banking' && (
                <div className="animate-fadeIn">
                  <h3 className="new-employee-section-header" style={{ marginBottom: '0.5rem', fontSize: '1.125rem', fontWeight: '600' }}>Bank Details</h3>
                  <p className="new-employee-section-description" style={{ marginBottom: '1.5rem', color: '#6b7280', fontSize: '0.875rem' }}>Update banking information for salary payment.</p>

                  <div className="update-employee-form-grid" style={{ gridTemplateColumns: '1fr' }}>
                    <div>
                      <label className="update-employee-field-label">Account Number</label>
                      <input type="text" value={formData.accountNumber || ''} onChange={(e) => handleInputChange('accountNumber', e.target.value)} onBlur={(e) => validateUniqueness('accountNumber', e.target.value)} className={`update-employee-input ${uniqueErrors.accountNumber ? 'input-error' : ''}`} />
                      {uniqueErrors.accountNumber && <span className="error-text">{uniqueErrors.accountNumber}</span>}
                    </div>
                  </div>

                  <div className="update-employee-form-grid">
                    <div>
                      <label className="update-employee-field-label">IFSC Code</label>
                      <input type="text" value={formData.ifsc || ''} onChange={(e) => handleInputChange('ifsc', e.target.value)} className="update-employee-input" maxLength="11" />
                    </div>
                    <div>
                      <label className="update-employee-field-label">Bank Name</label>
                      <input
                        type="text"
                        value={formData.bankName || ''}
                        onChange={(e) => {
                          const value = e.target.value;
                          // Allow only letters, spaces, and special characters (no pure numeric)
                          if (!/^\d+$/.test(value)) {
                            handleInputChange('bankName', value);
                          }
                        }}
                        pattern="[A-Za-z\s&.-]+"
                        title="Bank name should contain letters only, no numbers"
                        className="update-employee-input"
                      />
                    </div>
                  </div>
                </div>
              )}

              {/* Payroll Tab */}
              {activeTab === 'payroll' && (
                <div className="animate-fadeIn">
                  <h3 className="new-employee-section-header" style={{ marginBottom: '0.5rem', fontSize: '1.125rem', fontWeight: '600' }}>Payroll Information</h3>
                  <p className="new-employee-section-description" style={{ marginBottom: '1.5rem', color: '#6b7280', fontSize: '0.875rem' }}>Update compensation details (monthly amounts in ₹).</p>

                  <h4 style={{ marginBottom: '0.75rem', fontWeight: '600' }}>Salary Components (Monthly)</h4>
                  <div className="update-employee-form-grid">
                    <div>
                      <label className="update-employee-field-label">Basic (₹)</label>
                      <input type="number" value={formData.basic || ''} onChange={(e) => handleInputChange('basic', e.target.value)} className="update-employee-input" />
                    </div>
                    <div>
                      <label className="update-employee-field-label">DA - Dearness Allowance (₹)</label>
                      <input type="number" value={formData.da || ''} onChange={(e) => handleInputChange('da', e.target.value)} className="update-employee-input" />
                    </div>
                  </div>

                  <div className="update-employee-form-grid">
                    <div>
                      <label className="update-employee-field-label">HRA - House Rent Allowance (₹)</label>
                      <input type="number" value={formData.hra || ''} onChange={(e) => handleInputChange('hra', e.target.value)} className="update-employee-input" />
                    </div>
                    <div>
                      <label className="update-employee-field-label">Conveyance (₹)</label>
                      <input type="number" value={formData.conveyance || ''} onChange={(e) => handleInputChange('conveyance', e.target.value)} className="update-employee-input" />
                    </div>
                  </div>

                  <div className="update-employee-form-grid">
                    <div>
                      <label className="update-employee-field-label">Bonus (₹)</label>
                      <input type="number" value={formData.bonus || ''} onChange={(e) => handleInputChange('bonus', e.target.value)} className="update-employee-input" />
                    </div>
                    <div>
                      <label className="update-employee-field-label">Other Allowances (₹)</label>
                      <input type="number" value={formData.otherAllowances || ''} onChange={(e) => handleInputChange('otherAllowances', e.target.value)} className="update-employee-input" />
                    </div>
                  </div>

                  <h4 style={{ marginTop: '1.5rem', marginBottom: '0.75rem', fontWeight: '600' }}>Statutory Contribution Rates</h4>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '1rem', marginBottom: '1rem' }}>
                    <div>
                      <label className="update-employee-field-label">ESI (%)</label>
                      <input
                        type="number"
                        value={formData.esiPercent || ''}
                        onChange={(e) => handleInputChange('esiPercent', e.target.value)}
                        min="0"
                        max="100"
                        step="0.01"
                        className="update-employee-input"
                        placeholder="4"
                      />
                      <p style={{ fontSize: '0.75rem', color: '#6b7280', marginTop: '0.25rem' }}>
                        Default: 4%
                      </p>
                    </div>
                    <div>
                      <label className="update-employee-field-label">EPF (%)</label>
                      <input
                        type="number"
                        value={formData.epfPercent || ''}
                        onChange={(e) => handleInputChange('epfPercent', e.target.value)}
                        min="0"
                        max="100"
                        step="0.01"
                        className="update-employee-input"
                        placeholder="12"
                      />
                      <p style={{ fontSize: '0.75rem', color: '#6b7280', marginTop: '0.25rem' }}>
                        Default: 12%
                      </p>
                    </div>
                    <div>
                      <label className="update-employee-field-label">Employer PF (%)</label>
                      <input
                        type="number"
                        value={formData.employerPfPercent || ''}
                        onChange={(e) => handleInputChange('employerPfPercent', e.target.value)}
                        min="0"
                        max="100"
                        step="0.01"
                        className="update-employee-input"
                        placeholder="13"
                      />
                      <p style={{ fontSize: '0.75rem', color: '#6b7280', marginTop: '0.25rem' }}>
                        Default: 13%
                      </p>
                    </div>
                  </div>

                  <h4 style={{ marginTop: '1.5rem', marginBottom: '0.75rem', fontWeight: '600' }}>Auto-calculated Amounts</h4>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '1rem' }}>
                    <div>
                      <label className="update-employee-field-label">ESI Amount (₹)</label>
                      <input
                        type="text"
                        value={formData.esi || ''}
                        readOnly
                        className="update-employee-input"
                        style={{ backgroundColor: 'var(--theme-surfaceHover)', cursor: 'not-allowed' }}
                        placeholder="Auto-calculated"
                      />
                      <p style={{ fontSize: '0.75rem', color: '#6b7280', marginTop: '0.25rem' }}>
                        (Basic + DA) × {formData.esiPercent || 4}%
                      </p>
                    </div>
                    <div>
                      <label className="update-employee-field-label">EPF Amount (₹)</label>
                      <input
                        type="text"
                        value={formData.epf || ''}
                        readOnly
                        className="update-employee-input"
                        style={{ backgroundColor: 'var(--theme-surfaceHover)', cursor: 'not-allowed' }}
                        placeholder="Auto-calculated"
                      />
                      <p style={{ fontSize: '0.75rem', color: '#6b7280', marginTop: '0.25rem' }}>
                        (Basic + DA) × {formData.epfPercent || 12}%
                      </p>
                    </div>
                    <div>
                      <label className="update-employee-field-label">Employer PF Amount (₹)</label>
                      <input
                        type="text"
                        value={formData.employerPfAmount || ''}
                        readOnly
                        className="update-employee-input"
                        style={{ backgroundColor: 'var(--theme-surfaceHover)', cursor: 'not-allowed' }}
                        placeholder="Auto-calculated"
                      />
                      <p style={{ fontSize: '0.75rem', color: '#6b7280', marginTop: '0.25rem' }}>
                        (Basic + DA) × {formData.employerPfPercent || 13}%
                      </p>
                    </div>
                  </div>

                  <div className="update-employee-form-grid" style={{ gridTemplateColumns: '1fr' }}>
                    <div>
                      <label className="update-employee-field-label">Pay Frequency</label>
                      <select value={formData.payFrequency || ''} onChange={(e) => handleInputChange('payFrequency', e.target.value)} className="update-employee-select">
                        <option value="Weekly">Weekly</option>
                        <option value="Bi-Weekly">Bi-Weekly</option>
                        <option value="Monthly">Monthly</option>
                      </select>
                    </div>
                  </div>

                  <h4 style={{ marginTop: '1.5rem', marginBottom: '0.75rem', fontWeight: '600' }}>Add Other Allowances</h4>

                  {/* Display existing allowances */}
                  {otherAllowances.length > 0 && (
                    <div style={{ marginBottom: '1rem' }}>
                      {otherAllowances.map((allow, index) => (
                        <div key={allow.id || index} style={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center',
                          padding: '0.75rem',
                          backgroundColor: '#F3F4F6',
                          borderRadius: '0.5rem',
                          marginBottom: '0.5rem'
                        }}>
                          <div>
                            <strong>{allow.allowanceType === 'vehicle' ? 'Vehicle Allowances' : allow.allowanceType === 'meal' ? 'Meal Allowances' : allow.allowanceType === 'housing' ? 'Housing Allowances' : allow.customType}</strong>
                            <span style={{ marginLeft: '0.5rem', color: '#6B7280' }}>₹{allow.amount}</span>
                            {allow.notes && <span style={{ marginLeft: '0.5rem', fontSize: '0.875rem', color: '#9CA3AF' }}>({allow.notes})</span>}
                          </div>
                          <button
                            type="button"
                            onClick={async () => {
                              if (window.confirm('Are you sure you want to delete this allowance?')) {
                                try {
                                  await wagesAPI.deleteOtherAllowance(allow.id);
                                  setOtherAllowances(otherAllowances.filter(a => a.id !== allow.id));
                                  notifySavedAndReload('Allowance deleted successfully');
                                } catch (error) {
                                  console.error('Error deleting allowance:', error);
                                  alert('Error deleting allowance: ' + error.message);
                                }
                              }
                            }}
                            style={{
                              padding: '0.5rem',
                              backgroundColor: '#FEE2E2',
                              borderRadius: '0.375rem',
                              border: 'none',
                              cursor: 'pointer',
                              display: 'flex',
                              alignItems: 'center'
                            }}
                          >
                            <Trash2 size={16} color="#DC2626" />
                          </button>
                        </div>
                      ))}
                    </div>
                  )}

                  {/* Add new allowance form */}
                  <div style={{
                    padding: '1rem',
                    border: '2px dashed #D1D5DB',
                    borderRadius: '0.5rem',
                    marginBottom: '1rem'
                  }}>
                    <div className="update-employee-form-grid">
                      <div>
                        <label className="update-employee-field-label">Allowance Type</label>
                        <div style={{ position: 'relative' }}>
                          <select
                            value={newAllowance.allowanceType}
                            onChange={(e) => setNewAllowance({ ...newAllowance, allowanceType: e.target.value, customType: '' })}
                            className="update-employee-input"
                            style={{ paddingRight: '2.5rem', appearance: 'none' }}
                          >
                            <option value="" disabled>Select Allowances</option>
                            <option value="vehicle">Vehicle Allowances</option>
                            <option value="meal">Meal Allowances</option>
                            <option value="housing">Housing Allowances</option>
                            <option value="other">Other Allowances</option>
                          </select>
                          <ChevronDown size={18} style={{
                            position: 'absolute',
                            right: '0.75rem',
                            top: '50%',
                            transform: 'translateY(-50%)',
                            pointerEvents: 'none',
                            color: '#6B7280'
                          }} />
                        </div>
                      </div>

                      {newAllowance.allowanceType === 'other' && (
                        <div className="animate-fadeIn">
                          <label className="update-employee-field-label">Specify Other Type</label>
                          <input
                            type="text"
                            value={newAllowance.customType}
                            onChange={(e) => setNewAllowance({ ...newAllowance, customType: e.target.value })}
                            className="update-employee-input"
                            placeholder="Enter allowance type name"
                          />
                        </div>
                      )}

                      <div>
                        <label className="update-employee-field-label">Amount (₹)</label>
                        <input
                          type="number"
                          value={newAllowance.amount}
                          onChange={(e) => setNewAllowance({ ...newAllowance, amount: e.target.value })}
                          min="0"
                          step="0.01"
                          className="update-employee-input"
                          placeholder="0.00"
                        />
                      </div>
                    </div>

                    <div className="update-employee-form-grid" style={{ gridTemplateColumns: '1fr' }}>
                      <div>
                        <label className="update-employee-field-label">Notes (Optional)</label>
                        <input
                          type="text"
                          value={newAllowance.notes}
                          onChange={(e) => setNewAllowance({ ...newAllowance, notes: e.target.value })}
                          className="update-employee-input"
                          placeholder="Additional information"
                        />
                      </div>
                    </div>

                    <button
                      type="button"
                      onClick={async () => {
                        if (!newAllowance.allowanceType) {
                          alert('Please select an allowance type');
                          return;
                        }
                        if (!newAllowance.amount || newAllowance.amount <= 0) {
                          alert('Please enter a valid amount');
                          return;
                        }
                        if (newAllowance.allowanceType === 'other' && !newAllowance.customType.trim()) {
                          alert('Please specify the allowance type');
                          return;
                        }
                        if (!selectedEmployee || !selectedEstablishment) {
                          alert('Please select an employee first');
                          return;
                        }

                        try {
                          const allowanceData = {
                            employeeId: selectedEmployee,
                            establishmentId: parseInt(selectedEstablishment),
                            allowanceType: newAllowance.allowanceType === 'other' ? newAllowance.customType : newAllowance.allowanceType,
                            amount: parseFloat(newAllowance.amount),
                            effectiveFrom: newAllowance.effectiveFrom,
                            notes: newAllowance.notes,
                            status: 'active'
                          };
                          const response = await wagesAPI.addOtherAllowance(allowanceData);
                          notifySavedAndReload('Allowance added successfully');

                          // Reload allowances
                          await loadOtherAllowances(selectedEmployee);

                          // Reset form
                          setNewAllowance({
                            allowanceType: '',
                            customType: '',
                            amount: '',
                            effectiveFrom: new Date().toISOString().split('T')[0],
                            notes: ''
                          });
                        } catch (error) {
                          console.error('Error adding allowance:', error);
                          alert('Error adding allowance: ' + error.message);
                        }
                      }}
                      style={{
                        padding: '0.625rem 1.25rem',
                        backgroundColor: '#10B981',
                        color: 'white',
                        borderRadius: '0.5rem',
                        border: 'none',
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '0.5rem',
                        fontWeight: '500',
                        marginTop: '0.75rem'
                      }}
                    >
                      <Plus size={18} />
                      Add Allowance
                    </button>
                  </div>
                </div>
              )}

              {/* Deductions Tab */}
              {activeTab === 'deductions' && (
                <div className="animate-fadeIn">
                  <h3 className="new-employee-section-header" style={{ marginBottom: '0.5rem', fontSize: '1.125rem', fontWeight: '600' }}>Deductions</h3>
                  <p className="new-employee-section-description" style={{ marginBottom: '1.5rem', color: '#6b7280', fontSize: '0.875rem' }}>Configure statutory and other deductions (monthly amounts in ₹).</p>

                  <h4 style={{ marginTop: '1rem', marginBottom: '0.75rem', fontWeight: '600' }}>Statutory Deductions</h4>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '1rem', marginBottom: '1rem' }}>
                    <div>
                      <label className="update-employee-field-label">ESI (%)</label>
                      <input
                        type="text"
                        value={formData.esiPercent || ''}
                        readOnly
                        className="update-employee-input"
                        style={{ backgroundColor: 'var(--theme-surfaceHover)', cursor: 'not-allowed' }}
                        placeholder="4"
                      />
                      <p style={{ fontSize: '0.75rem', color: '#6b7280', marginTop: '0.25rem' }}>
                        Configure in Payroll (Default: 4%)
                      </p>
                    </div>
                    <div>
                      <label className="update-employee-field-label">EPF (%)</label>
                      <input
                        type="text"
                        value={formData.epfPercent || ''}
                        readOnly
                        className="update-employee-input"
                        style={{ backgroundColor: 'var(--theme-surfaceHover)', cursor: 'not-allowed' }}
                        placeholder="12"
                      />
                      <p style={{ fontSize: '0.75rem', color: '#6b7280', marginTop: '0.25rem' }}>
                        Configure in Payroll (Default: 12%)
                      </p>
                    </div>
                    <div>
                      <label className="update-employee-field-label">Employer PF (%)</label>
                      <input
                        type="text"
                        value={formData.employerPfPercent || ''}
                        readOnly
                        className="update-employee-input"
                        style={{ backgroundColor: 'var(--theme-surfaceHover)', cursor: 'not-allowed' }}
                        placeholder="13"
                      />
                      <p style={{ fontSize: '0.75rem', color: '#6b7280', marginTop: '0.25rem' }}>
                        Configure in Payroll (Default: 13%)
                      </p>
                    </div>
                  </div>

                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '1rem' }}>
                    <div>
                      <label className="update-employee-field-label">ESI Amount (₹)</label>
                      <input
                        type="text"
                        value={formData.esi || ''}
                        readOnly
                        className="update-employee-input"
                        style={{ backgroundColor: 'var(--theme-surfaceHover)', cursor: 'not-allowed' }}
                        placeholder="Auto-calculated"
                      />
                      <p style={{ fontSize: '0.75rem', color: '#6b7280', marginTop: '0.25rem' }}>
                        (Basic + DA) × {formData.esiPercent || 4}%
                      </p>
                    </div>
                    <div>
                      <label className="update-employee-field-label">EPF Amount (₹)</label>
                      <input
                        type="text"
                        value={formData.epf || ''}
                        readOnly
                        className="update-employee-input"
                        style={{ backgroundColor: 'var(--theme-surfaceHover)', cursor: 'not-allowed' }}
                        placeholder="Auto-calculated"
                      />
                      <p style={{ fontSize: '0.75rem', color: '#6b7280', marginTop: '0.25rem' }}>
                        (Basic + DA) × {formData.epfPercent || 12}%
                      </p>
                    </div>
                    <div>
                      <label className="update-employee-field-label">Employer PF Amount (₹)</label>
                      <input
                        type="text"
                        value={formData.employerPfAmount || ''}
                        readOnly
                        className="update-employee-input"
                        style={{ backgroundColor: 'var(--theme-surfaceHover)', cursor: 'not-allowed' }}
                        placeholder="Auto-calculated"
                      />
                      <p style={{ fontSize: '0.75rem', color: '#6b7280', marginTop: '0.25rem' }}>
                        (Basic + DA) × {formData.employerPfPercent || 13}%
                      </p>
                    </div>
                  </div>

                  <div className="update-employee-form-grid">
                    <div>
                      <label className="update-employee-field-label">PT - Professional Tax (₹)</label>
                      <input type="number" value={formData.pt || ''} onChange={(e) => handleInputChange('pt', e.target.value)} className="update-employee-input" />
                    </div>
                    <div>
                      <label className="update-employee-field-label">TDS - Tax Deducted at Source (₹)</label>
                      <input type="number" value={formData.tds || ''} onChange={(e) => handleInputChange('tds', e.target.value)} className="update-employee-input" />
                    </div>
                  </div>

                  <h4 style={{ marginTop: '1.5rem', marginBottom: '0.75rem', fontWeight: '600' }}>Add Deductions</h4>

                  {/* Display existing deductions */}
                  {otherDeductions.length > 0 && (
                    <div style={{ marginBottom: '1rem' }}>
                      {otherDeductions.map((ded, index) => (
                        <div key={ded.id || index} style={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center',
                          padding: '0.75rem',
                          backgroundColor: '#F3F4F6',
                          borderRadius: '0.5rem',
                          marginBottom: '0.5rem'
                        }}>
                          <div>
                            <strong>{ded.deductionType === 'society' ? 'Society' : ded.deductionType === 'insurance' ? 'Insurance' : ded.customType}</strong>
                            <span style={{ marginLeft: '0.5rem', color: '#6B7280' }}>₹{ded.amount}</span>
                            {ded.notes && <span style={{ marginLeft: '0.5rem', fontSize: '0.875rem', color: '#9CA3AF' }}>({ded.notes})</span>}
                          </div>
                          <button
                            type="button"
                            onClick={async () => {
                              if (window.confirm('Are you sure you want to delete this deduction?')) {
                                try {
                                  await wagesAPI.deleteOtherDeduction(ded.id);
                                  setOtherDeductions(otherDeductions.filter(d => d.id !== ded.id));
                                  notifySavedAndReload('Deduction deleted successfully');
                                } catch (error) {
                                  console.error('Error deleting deduction:', error);
                                  alert('Error deleting deduction: ' + error.message);
                                }
                              }
                            }}
                            style={{
                              padding: '0.5rem',
                              backgroundColor: '#FEE2E2',
                              borderRadius: '0.375rem',
                              border: 'none',
                              cursor: 'pointer',
                              display: 'flex',
                              alignItems: 'center'
                            }}
                          >
                            <Trash2 size={16} color="#DC2626" />
                          </button>
                        </div>
                      ))}
                    </div>
                  )}

                  {/* Add new deduction form */}
                  <div style={{
                    padding: '1rem',
                    border: '2px dashed #D1D5DB',
                    borderRadius: '0.5rem',
                    marginBottom: '1rem'
                  }}>
                    <div className="update-employee-form-grid">
                      <div>
                        <label className="update-employee-field-label">Deduction Type</label>
                        <div style={{ position: 'relative' }}>
                          <select
                            value={newDeduction.deductionType}
                            onChange={(e) => setNewDeduction({ ...newDeduction, deductionType: e.target.value, customType: '' })}
                            className="update-employee-input"
                            style={{ paddingRight: '2.5rem', appearance: 'none' }}
                          >
                            <option value="" disabled>Select Deduction Type</option>
                            <option value="society">Society</option>
                            <option value="insurance">Insurance</option>
                            <option value="other">Other</option>
                          </select>
                          <ChevronDown size={18} style={{
                            position: 'absolute',
                            right: '0.75rem',
                            top: '50%',
                            transform: 'translateY(-50%)',
                            pointerEvents: 'none',
                            color: '#6B7280'
                          }} />
                        </div>
                      </div>

                      {newDeduction.deductionType === 'other' && (
                        <div className="animate-fadeIn">
                          <label className="update-employee-field-label">Specify Other Type</label>
                          <input
                            type="text"
                            value={newDeduction.customType}
                            onChange={(e) => setNewDeduction({ ...newDeduction, customType: e.target.value })}
                            className="update-employee-input"
                            placeholder="Enter deduction type name"
                          />
                        </div>
                      )}

                      <div>
                        <label className="update-employee-field-label">Amount (₹)</label>
                        <input
                          type="number"
                          value={newDeduction.amount}
                          onChange={(e) => setNewDeduction({ ...newDeduction, amount: e.target.value })}
                          min="0"
                          step="0.01"
                          className="update-employee-input"
                          placeholder="0.00"
                        />
                      </div>
                    </div>

                    <div className="update-employee-form-grid" style={{ gridTemplateColumns: '1fr' }}>
                      <div>
                        <label className="update-employee-field-label">Notes (Optional)</label>
                        <input
                          type="text"
                          value={newDeduction.notes}
                          onChange={(e) => setNewDeduction({ ...newDeduction, notes: e.target.value })}
                          className="update-employee-input"
                          placeholder="Additional information"
                        />
                      </div>
                    </div>

                    <button
                      type="button"
                      onClick={async () => {
                        if (!newDeduction.deductionType) {
                          alert('Please select a deduction type');
                          return;
                        }
                        if (!newDeduction.amount || newDeduction.amount <= 0) {
                          alert('Please enter a valid amount');
                          return;
                        }
                        if (newDeduction.deductionType === 'other' && !newDeduction.customType.trim()) {
                          alert('Please specify the deduction type');
                          return;
                        }
                        if (!selectedEmployee || !selectedEstablishment) {
                          alert('Please select an employee first');
                          return;
                        }

                        try {
                          const deductionData = {
                            employeeId: selectedEmployee,
                            establishmentId: parseInt(selectedEstablishment),
                            deductionType: newDeduction.deductionType === 'other' ? newDeduction.customType : newDeduction.deductionType,
                            amount: parseFloat(newDeduction.amount),
                            effectiveFrom: newDeduction.effectiveFrom,
                            notes: newDeduction.notes,
                            status: 'active'
                          };
                          const response = await wagesAPI.addOtherDeduction(deductionData);
                          notifySavedAndReload('Deduction added successfully');

                          // Reload deductions
                          await loadOtherDeductions(selectedEmployee);

                          // Reset form
                          setNewDeduction({
                            deductionType: '',
                            customType: '',
                            amount: '',
                            effectiveFrom: new Date().toISOString().split('T')[0],
                            notes: ''
                          });
                        } catch (error) {
                          console.error('Error adding deduction:', error);
                          alert('Error adding deduction: ' + error.message);
                        }
                      }}
                      style={{
                        padding: '0.625rem 1.25rem',
                        backgroundColor: '#10B981',
                        color: 'white',
                        borderRadius: '0.5rem',
                        border: 'none',
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '0.5rem',
                        fontWeight: '500',
                        marginTop: '0.75rem'
                      }}
                    >
                      <Plus size={18} />
                      Add Deduction
                    </button>
                  </div>

                  <div style={{ marginTop: '1.5rem', padding: '1rem', border: '1px solid #BBF7D0', borderRadius: '0.5rem', backgroundColor: '#F0FDF4' }}>
                    <p style={{ fontSize: '0.875rem', color: '#166534' }}>
                      <strong>Note:</strong> ESI and EPF amounts are auto-calculated based on Basic + DA and their respective configurable percentages (defaults: ESI = 4%, EPF = 12%, Employer PF = 13%). Configure these percentages in the Payroll section. Other statutory deductions (PT, TDS) should be entered as per applicable laws.
                    </p>
                  </div>
                </div>
              )}

              {/* Leaves Tab */}
              {activeTab === 'leaves' && (
                <div className="animate-fadeIn">
                  <LeaveConfigForm
                    formData={formData}
                    setFormData={setFormData}
                    showTitle={true}
                    compact={false}
                  />
                </div>
              )}

              {/* Action Buttons */}
              <div className="new-employee-actions" style={{ display: 'flex', justifyContent: 'space-between', marginTop: '2rem', gap: '1rem' }}>
                <button
                  onClick={handlePrevious}
                  disabled={activeTab === 'personal'}
                  className="new-employee-btn-previous"
                  style={{
                    padding: '0.75rem 1.5rem',
                    background: activeTab === 'personal' ? '#f8f9fa' : '#ffffff',
                    color: activeTab === 'personal' ? '#9ca3af' : '#374151',
                    border: '1px solid #e5e7eb',
                    borderRadius: '8px',
                    cursor: activeTab === 'personal' ? 'not-allowed' : 'pointer',
                    fontSize: '0.875rem',
                    fontWeight: '500'
                  }}
                >
                  Previous
                </button>

                <div style={{ display: 'flex', gap: '0.75rem' }}>
                  {/* Save Button - Always visible */}
                  <button
                    onClick={handleSubmit}
                    disabled={isSubmitting}
                    className="new-employee-btn-create"
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '0.5rem',
                      padding: '0.75rem 1.5rem',
                      background: '#10b981',
                      color: 'white',
                      border: 'none',
                      borderRadius: '8px',
                      cursor: isSubmitting ? 'not-allowed' : 'pointer',
                      fontSize: '0.875rem',
                      fontWeight: '500'
                    }}
                  >
                    {isSubmitting ? (
                      <>
                        <div className="update-employee-spinner"></div>
                        Saving...
                      </>
                    ) : (
                      <>
                        <Save size={16} />
                        Save
                      </>
                    )}
                  </button>

                  {/* Next Button - Only if not last tab */}
                  {!isLastTab && (
                    <button
                      onClick={handleNext}
                      className="new-employee-btn-next"
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '0.5rem',
                        padding: '0.75rem 1.5rem',
                        background: '#3b82f6',
                        color: 'white',
                        border: 'none',
                        borderRadius: '8px',
                        cursor: 'pointer',
                        fontSize: '0.875rem',
                        fontWeight: '500'
                      }}
                    >
                      Next
                      <ChevronRight size={16} />
                    </button>
                  )}
                </div>
              </div>
            </div>
              </>
            )}
          </div>
        )}

      </div>
    </div>
  );
};

export default UpdateEmployee;